/**
 * @file 校区管理主面板
 * @description 页头操作 + mock 列表 + 行操作（下拉菜单固定右侧）。
 *
 * @whenToUse 仅由 CampusPage 组合。
 * @whenNotToUse 其他业务列表页。
 */

import { useState } from 'react'
import { MoreHorizontal, Plus } from 'lucide-react'
import { confirm } from '@/components/ui/confirm.tsx'
import { Button } from '@/components/ui/button.tsx'
import { type DataTableColumn } from '@/components/ui/data-table.tsx'
import { RequestTable } from '@/components/ui/request-table.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import { toast } from '@/components/ui/toast.tsx'
import {
  CampusMemberDialog,
  CampusRechargeDialog,
  CampusRechargeRecordsDialog,
  type CampusPointsDialogMode,
} from '@/components/pages/campus-page/campus-action-dialogs.tsx'
import { CampusFormDialog } from '@/components/pages/campus-page/campus-form-dialog.tsx'
import { CampusSubOverview } from '@/components/pages/campus-page/campus-sub-overview.tsx'
import {
  MOCK_CAMPUSES,
  MOCK_RECHARGE_RECORDS,
  MOCK_STUDENTS,
  MOCK_TEACHERS,
  type CampusFormValues,
  type CampusMember,
  type CampusRechargeRecord,
  type CampusRow,
} from '@/components/pages/campus-page/mock.ts'
import { usePermissionSimStore } from '@/stores/permission-sim-store.ts'

type MemberDialogState = {
  campus: CampusRow
  kind: 'teacher' | 'student'
  members: CampusMember[]
}

/**
 * 格式化本地时间字符串
 * @param date 时间
 */
function formatDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

/**
 * 校区管理面板（含创建与行操作）
 */
export function CampusPanel() {
  const role = usePermissionSimStore((s) => s.role)
  const isSubAccount = role === 'sub'
  const [rows, setRows] = useState<CampusRow[]>(MOCK_CAMPUSES)
  const [rechargeRecords, setRechargeRecords] = useState(MOCK_RECHARGE_RECORDS)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<CampusRow | null>(null)
  const [active, setActive] = useState<CampusRow | null>(null)
  const [pointsOpen, setPointsOpen] = useState(false)
  const [pointsMode, setPointsMode] =
    useState<CampusPointsDialogMode>('recharge')
  const [recordsOpen, setRecordsOpen] = useState(false)
  const [memberDialog, setMemberDialog] = useState<MemberDialogState | null>(
    null,
  )

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (row: CampusRow) => {
    setEditing(row)
    setFormOpen(true)
  }

  const handleFormSubmit = (values: CampusFormValues) => {
    if (editing) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === editing.id
            ? {
                ...row,
                name: values.name.trim(),
                account: values.account.trim(),
                points: values.points,
              }
            : row,
        ),
      )
      toast.success('已保存校区')
      return
    }

    const id = `campus-${Date.now()}`
    setRows((prev) => [
      {
        id,
        name: values.name.trim(),
        account: values.account.trim(),
        points: values.points,
        teacherCount: 0,
        studentCount: 0,
        createdAt: formatDateTime(new Date()),
        enabled: true,
      },
      ...prev,
    ])
    toast.success('已创建校区')
  }

  const toggleEnabled = (row: CampusRow) => {
    const nextEnabled = !row.enabled
    setRows((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, enabled: nextEnabled } : item,
      ),
    )
    toast.success(nextEnabled ? '已启用' : '已禁用', row.name)
  }

  const handleDelete = async (row: CampusRow) => {
    const ok = await confirm({
      title: '删除校区？',
      description: `将删除「${row.name}」，此操作不可恢复。`,
      confirmText: '删除',
      danger: true,
    })
    if (!ok) return
    setRows((prev) => prev.filter((item) => item.id !== row.id))
    toast.success('已删除', row.name)
  }

  const handleResetPassword = async (row: CampusRow) => {
    const ok = await confirm({
      title: '重置密码？',
      description: `将「${row.name}」主账号 ${row.account} 的密码重置为 123456，确认是否重置？`,
      confirmText: '重置',
    })
    if (!ok) return
    toast.success('密码已重置为 123456', row.name)
  }

  const openPointsDialog = (row: CampusRow, mode: CampusPointsDialogMode) => {
    setActive(row)
    setPointsMode(mode)
    setPointsOpen(true)
  }

  const handlePointsConfirm = (points: number, remark: string) => {
    if (!active) return
    const campusId = active.id
    const delta = pointsMode === 'deduct' ? -points : points
    setRows((prev) =>
      prev.map((row) =>
        row.id === campusId ? { ...row, points: row.points + delta } : row,
      ),
    )
    const record: CampusRechargeRecord = {
      id: `r-${Date.now()}`,
      points: delta,
      createdAt: formatDateTime(new Date()),
      remark,
    }
    setRechargeRecords((prev) => ({
      ...prev,
      [campusId]: [record, ...(prev[campusId] ?? [])],
    }))
    toast.success(
      pointsMode === 'deduct' ? '扣除成功' : '充值成功',
      `${delta > 0 ? '+' : ''}${delta} 点`,
    )
  }

  const columns: DataTableColumn<CampusRow>[] = [
    {
      key: 'name',
      title: '校区名称',
      dataIndex: 'name',
      minWidth: 120,
    },
    {
      key: 'account',
      title: '主账号',
      dataIndex: 'account',
      minWidth: 120,
    },
    {
      key: 'points',
      title: '点数',
      dataIndex: 'points',
      minWidth: 100,
    },
    {
      key: 'teacherCount',
      title: '教师人数',
      minWidth: 100,
      render: (row) => (
        <button
          type="button"
          className="text-primary transition-opacity duration-150 hover:opacity-90"
          onClick={() =>
            setMemberDialog({
              campus: row,
              kind: 'teacher',
              members: MOCK_TEACHERS[row.id] ?? [],
            })
          }
        >
          {row.teacherCount}
        </button>
      ),
    },
    {
      key: 'studentCount',
      title: '学员人数',
      minWidth: 100,
      render: (row) => (
        <button
          type="button"
          className="text-primary transition-opacity duration-150 hover:opacity-90"
          onClick={() =>
            setMemberDialog({
              campus: row,
              kind: 'student',
              members: MOCK_STUDENTS[row.id] ?? [],
            })
          }
        >
          {row.studentCount}
        </button>
      ),
    },
    {
      key: 'createdAt',
      title: '创建时间',
      dataIndex: 'createdAt',
      minWidth: 180,
    },
    {
      key: 'actions',
      title: '操作',
      fixed: 'right',
      width: 72,
      render: (row) => (
        <DropdownMenu
          align="end"
          trigger={
            <IconButton
              variant="ghost"
              aria-label={`${row.name} 操作`}
              icon={<MoreHorizontal className="size-4" aria-hidden />}
            />
          }
          items={[
            {
              key: 'reset',
              label: '重置密码',
              onSelect: () => {
                void handleResetPassword(row)
              },
            },
            {
              key: 'toggle',
              label: row.enabled ? '禁用' : '启用',
              onSelect: () => toggleEnabled(row),
            },
            {
              key: 'edit',
              label: '编辑',
              onSelect: () => openEdit(row),
            },
            { key: 'div-2', type: 'divider' },
            {
              key: 'recharge',
              label: '充值',
              onSelect: () => openPointsDialog(row, 'recharge'),
            },
            {
              key: 'deduct',
              label: '扣除',
              disabled: row.points <= 100,
              onSelect: () => openPointsDialog(row, 'deduct'),
            },
            {
              key: 'records',
              label: '充值记录',
              onSelect: () => {
                setActive(row)
                setRecordsOpen(true)
              },
            },
            { key: 'div-1', type: 'divider' },
            {
              key: 'delete',
              label: '删除',
              danger: true,
              onSelect: () => {
                void handleDelete(row)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="校区管理"
        description={
          isSubAccount
            ? '查看本校区教师、学员与点数概况。'
            : '管理子校区账号、点数与启用状态。'
        }
        extra={
          isSubAccount ? undefined : (
            <Button
              icon={<Plus className="size-4" aria-hidden />}
              onClick={openCreate}
            >
              创建校区
            </Button>
          )
        }
      />

      <div className="mt-6">
        {isSubAccount ? (
          <CampusSubOverview campus={rows[0] ?? MOCK_CAMPUSES[0]} />
        ) : (
          <RequestTable columns={columns} data={rows} pagination />
        )}
      </div>

      {/* 创建/编辑校区对话框 */}
      <CampusFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSubmit={handleFormSubmit}
      />

      {/* 充值 / 扣除对话框 */}
      <CampusRechargeDialog
        open={pointsOpen}
        onOpenChange={setPointsOpen}
        campus={active}
        mode={pointsMode}
        onConfirm={handlePointsConfirm}
      />

      {/* 充值记录对话框 */}
      <CampusRechargeRecordsDialog
        open={recordsOpen}
        onOpenChange={setRecordsOpen}
        campus={active}
        records={active ? (rechargeRecords[active.id] ?? []) : []}
      />

      {/* 教师/学员对话框 */}
      <CampusMemberDialog
        open={Boolean(memberDialog)}
        onOpenChange={(open) => {
          if (!open) setMemberDialog(null)
        }}
        campus={memberDialog?.campus ?? null}
        kind={memberDialog?.kind ?? 'teacher'}
        members={memberDialog?.members ?? []}
      />
    </>
  )
}
